/**
 * LINE ニュース画面：ニュース一覧と編集のみ。
 * テンプレート / プレセット管理は「🧠 プロンプト」大項目（/prompts/templates, /prompts/presets）に統合済み。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10 / docs/01_spec/spec_common.md C-X 統一プロンプト管理
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useCreateLineNews } from '@/hooks/useLineNews';

import { NewsEditorView } from './NewsEditorView';
import { NewsListView } from './NewsListView';
import { PlanQuotaBadge } from './PlanQuotaBadge';

export function LineNewsPage() {
  const { user } = useAuth();
  const createMutation = useCreateLineNews();
  const [params, setParams] = useSearchParams();
  const [editingId, setEditingId] = useState<string | null>(
    params.get('editingId'),
  );

  useEffect(() => {
    const urlEditingId = params.get('editingId');
    if (editingId && urlEditingId !== editingId) {
      const next = new URLSearchParams(params);
      next.set('editingId', editingId);
      setParams(next, { replace: true });
    } else if (!editingId && urlEditingId) {
      const next = new URLSearchParams(params);
      next.delete('editingId');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  useEffect(() => {
    const urlEditingId = params.get('editingId');
    if (urlEditingId && urlEditingId !== editingId) {
      setEditingId(urlEditingId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleCreate = async () => {
    try {
      const id = await createMutation.mutateAsync({
        title: '無題',
        sections: [],
        status: 'draft',
        createdBy: user?.uid ?? '',
      });
      setEditingId(id);
    } catch (err) {
      toast.error(`作成失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (editingId) {
    return (
      <Screen
        title="LINE ニュース 編集"
        description="セクションを編集して保存・配信予約してください"
        actions={<PlanQuotaBadge />}
      >
        <NewsEditorView newsId={editingId} onBack={() => setEditingId(null)} />
      </Screen>
    );
  }

  return (
    <Screen
      title="LINE ニュース"
      description="LINE 公式アカウントから友だちにニュースを配信。Multicast または Broadcast で送信。"
      actions={<PlanQuotaBadge />}
    >
      <NewsListView
        onCreate={handleCreate}
        onSelect={(n) => setEditingId(n.id)}
      />
    </Screen>
  );
}
